import {type ReactNode, useId, useRef} from 'react';

import {photoUrl} from '../../../shared/api/usePeople';
import {Avatar} from '../../../shared/components/avatar';
import {Bone} from '../../../shared/components/bone';
import {InLayer} from '../../../shared/components/layer';
import {ErrorNotice, Notice} from '../../../shared/components/notice';
import {SlackText} from '../../../shared/components/slackText';
import {useDismiss} from '../../../shared/hooks/useDismiss';
import {PaperButton} from '../../../shared/sticker/stickers';
import type {Shoutout} from '../../../shared/types';
import {MAX_OPTIONS} from '../slackImport';

export type SlackImportDialogProps = {
    since: string;
    onSince: (since: string) => void;
    onLoad: () => void;
    loading: boolean;
    error: unknown;
    /** Null until a date has been loaded. */
    shoutouts: Shoutout[] | null;
    loadedSince: string | null;
    picked: ReadonlySet<string>;
    /** How many picked messages are over the vote's option limit; zero when they fit. */
    tooMany: number;
    onToggle: (ts: string) => void;
    onAdd: () => void;
    onClose: () => void;
};

const day = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {day: 'numeric', month: 'short'});

const Row = ({
    shoutout,
    checked,
    onToggle,
}: {
    shoutout: Shoutout;
    checked: boolean;
    onToggle: () => void;
}) => (
    <li>
        <label className="bt-slack__row">
            <input type="checkbox" checked={checked} onChange={onToggle} />
            <div className="bt-slack__body">
                <div className="bt-slack__people">
                    {shoutout.people.map((p) => (
                        <span key={p.id} className="bt-chip">
                            <Avatar
                                src={p.has_photo ? photoUrl(p.id) : undefined}
                                name={p.name}
                                size={20}
                            />
                            <span className="bt-chip__name">{p.name}</span>
                        </span>
                    ))}
                    {shoutout.people.length === 0 && (
                        <span className="bt-slack__note">Nobody tagged: add a title after</span>
                    )}
                    {shoutout.unmatched_names.length > 0 && (
                        <span
                            className="bt-slack__note"
                            title={shoutout.unmatched_names.join(', ')}
                        >
                            {shoutout.unmatched_names.length} not in directory
                        </span>
                    )}
                </div>
                <SlackText className="bt-slack__text" text={shoutout.text} />
            </div>
            <span className="bt-slack__date">{day(shoutout.posted_at)}</span>
        </label>
    </li>
);

const Results = (props: SlackImportDialogProps): ReactNode => {
    if (props.loading)
        return (
            <div className="bt-slack__list" role="status" aria-label="Loading messages">
                <Bone block height={64} />
                <Bone block height={64} />
                <Bone block height={64} />
            </div>
        );
    if (props.error)
        return <ErrorNotice title="Could not read the shoutout channel" error={props.error} />;
    if (props.shoutouts === null) return null;
    if (props.shoutouts.length === 0)
        return <Notice message={`No messages since ${props.loadedSince}`} />;
    return (
        <ul className="bt-slack__list">
            {props.shoutouts.map((s) => (
                <Row
                    key={s.ts}
                    shoutout={s}
                    checked={props.picked.has(s.ts)}
                    onToggle={() => props.onToggle(s.ts)}
                />
            ))}
        </ul>
    );
};

/** Picks shoutout messages from Slack to add as options. */
export const SlackImportDialog = (props: SlackImportDialogProps) => {
    const card = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const dateId = useId();
    useDismiss(card, true, props.onClose);

    const blanks =
        props.shoutouts?.filter((s) => props.picked.has(s.ts) && s.people.length === 0).length ?? 0;
    const count = props.picked.size;

    return (
        <InLayer>
            <div className="sd-dim">
                <div
                    ref={card}
                    className="sd-dialog bt-slack"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                >
                    <h2 id={titleId} className="sd-dialog-title">
                        Import from Slack
                    </h2>
                    <div className="bt-slack__since">
                        <label className="bt-label" htmlFor={dateId}>
                            Messages since
                        </label>
                        <input
                            id={dateId}
                            type="date"
                            className="bt-field"
                            value={props.since}
                            onChange={(e) => props.onSince(e.target.value)}
                        />
                        <PaperButton
                            variant="ghost"
                            className="bt-button--small"
                            disabled={props.since === '' || props.loading}
                            onClick={props.onLoad}
                        >
                            Load messages
                        </PaperButton>
                    </div>
                    <Results {...props} />
                    {blanks > 0 && (
                        <p className="bt-help">
                            {blanks === 1 ? '1 message has' : `${blanks} messages have`} nobody
                            tagged. Give those options a title, or they will not be saved.
                        </p>
                    )}
                    {props.tooMany > 0 && (
                        <Notice
                            tone="warning"
                            message={`A vote holds at most ${MAX_OPTIONS} options. Untick ${props.tooMany} to add the rest.`}
                        />
                    )}
                    <div className="sd-dialog-actions">
                        <PaperButton variant="ghost" onClick={props.onClose}>
                            Cancel
                        </PaperButton>
                        <PaperButton
                            disabled={count === 0 || props.tooMany > 0}
                            onClick={props.onAdd}
                        >
                            Add {count} {count === 1 ? 'option' : 'options'}
                        </PaperButton>
                    </div>
                </div>
            </div>
        </InLayer>
    );
};
